<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application?.localName || application?.title"
      role="navigation"
    />
    <TitleAndDescription
      :application="application"
      :local-title="
        $t('dataTypeAuthorizations.title', {
          user: currentUser[0] ? currentUser[0].label : currentUser.login,
        })
      "
      :local-description="currentAuthorization.description"
      :local-name="application.localName || application.title"
    />
    <LoadingAnimate
      v-if="isLoading"
      :size="'is-medium'"
    />
    <div
      v-else
      class="contentView rightsRequestForm"
    >
      <b-message :title="$t('requestAuthorization.info-auth.title')">
        {{ $t('requestAuthorization.info-auth.message') }}
      </b-message>
      <Form
        ref="observer"
        v-slot="{ handleSubmit }"
      >
        <FieldsForm
          :application="application"
          :comment="comment"
          :description="description"
          :fields="fields"
          :format="format"
          :show-comment="Object.keys(format).length === 0"
          path-for-key="rightsrequest.fields"
          :max-length="1000"
          :comment-min-length="COMMENT_MIN_LENGTH"
          label-comment="requestAuthorization.label-detail-auth"
          @update:fields="updateFields"
          @update:comment="updateComment"
        />
        <AuthorizationTableForDatatype
          :application="application"
          :application-name="applicationName"
          :authorization-id="authorizationId"
          :authorizations="authorizations"
          :current-authorization="currentAuthorization"
          :datatypes="datatypes"
          :has-dependencies="hasDependencies"
          :initialized="initialized"
          :references="references"
          :references-scopes="referencesScopes"
          title-collapse="requestAuthorization.right-request"
          :is-data-collapse-open="true"
          @update:authorization="updateAuthorization"
        />
        <div class="buttons">
          <b-button
            v-if="canCreateApplication"
            class="column is-offset-10 is-2 btnValidate"
            icon-left="plus"
            type="is-warning"
            outlined
            expanded
            rounded
            @click="handleSubmit(confirmGrantAuthorization)"
          />
          <b-button
            v-else-if="'new' === authorizationId"
            class="column is-offset-10 is-2 btnValidate"
            icon-left="floppy-disk"
            expanded
            rounded
            type="is-dark"
            @click="handleSubmit(createRequest)"
          />
        </div>
      </Form>
    </div>
  </PageView>
</template>

<script lang="ts">
import { Form } from "vee-validate";
import SubMenu from "@/components/common/SubMenu.vue";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import PageView from "../../common/PageView.vue";
import AuthorizationTableForDatatype from "@/components/common/AuthorizationTableForDatatype.vue";
import FieldsForm from "@/components/common/provider/FieldsForm.vue";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import services from "@/composable/services";
import { computed, onMounted, ref, watch } from "vue";
import useObject from "@/composable/components/object";
import i18n from "@/i18n";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import useArray from "@/composable/components/array";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import "@/style/views/authorization/right_request/AuthorizationsRightsRequestInfoView.scss";
import { BButton, BCollapse, BIcon, BMessage, BNotification } from "buefy";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import {
  AuthorizationParsed,
  Configuration,
  CreateAuthorizationRequest,
  GetAuthorizationResult, LoginAdminResult,
  Node, User,
} from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";
import {
  CommentErrorKey,
  DEFAULT_COMMENT_MIN_LENGTH,
} from "@/composable/validation/commentValidator";

export default {
  name: "AuthorizationsRightsRequestInfoView",
  components: {
    BMessage,
    BNotification,
    BIcon,
    BCollapse,
    BButton,
    TitleAndDescription,
    LoadingAnimate,
    AuthorizationTableForDatatype,
    PageView,
    SubMenu,
    Form,
    FieldsForm,
  },
  props: {
    applicationName: {
      type: String,
      required: true,
    },
    authorizationId: {
      type: String,
      default: "new",
    },
  },
  setup(props) {
    const { t } = useI18n();
    const router = useRouter();
    let { reactiveObject: application, doChangeObject: changeApplication } = useObject(
      new LocalApplicationResult(),
    );
    const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();
    const isLoading = ref(false);
    const canCreateApplication = ref(false);
    const datatypes = ref({ withScope: {}, withoutScope: {} });
    const references = ref({ withScope: {}, withoutScope: {} });
    const { doChangeObject: changeDatas } = useObject({});
    const { reactiveObject: configuration, doChangeObject: changeConfiguration } = useObject({} as Configuration);
    const { reactiveObject: authorization, doChangeObject: changeAuthorization } = useObject({} as GetAuthorizationResult);
    const { reactiveObject: format, doChangeObject: changeFormat } = useObject({});
    const { reactiveObject: fields, doChangeObject: changeFields } = useObject({});
    const { reactiveObject: authorizations } = useObject({} as AuthorizationParsed);
    const currentAuthorization = ref({} as GetAuthorizationResult);
    const initialized = ref(false);
    const referencesScopes: any = ref({});
    let valid: boolean = false;
    /**
     * Mémorise la dernière clé d'erreur i18n émise par FieldsForm pour
     * le commentaire ( détail de la demande ) . Permet à
     * {@code createRequest} d'afficher un toast précis ( "obligatoire"
     * vs "trop court" ) au lieu d'un return silencieux historique .
     * {@code null} = valide . Le type est importé du helper pur pour
     * garder la liste des clés synchronisée à un seul endroit .
     */
    const commentErrorKey = ref<CommentErrorKey>(null);
    const commentErrorParams = ref<Record<string, number>>({});
    /**
     * Longueur minimale du commentaire de demande , centralisée dans
     * {@link DEFAULT_COMMENT_MIN_LENGTH} ( helper pur , partagée avec
     * tests + computed FieldsForm ) . Pas de redéfinition locale -
     * une seule source de vérité .
     */
    const COMMENT_MIN_LENGTH = DEFAULT_COMMENT_MIN_LENGTH;
    const { reactiveObject: period } = useObject();
    let startDate = ref(null);
    let endDate = ref(null);
    const description = ref("" as string);
    const comment = ref("" as string);
    const dependencies = ref({});
    const currentUser = ref([] as LoginAdminResult[] | User | any);
    const hasDependencies = computed(() => {
      const result = new Set();
      const authorizations = currentAuthorization.value.authorizations;
      const hierarchicalNodes = configuration.hierarchicalNodes;

      for (const [datatype, auth] of Object.entries(authorizations || {}) as [string, AuthorizationParsed][]) {
        if (auth.operationTypes && auth.operationTypes.length > 0) {
          // Trouver le nœud correspondant dans la configuration
          const node = hierarchicalNodes.find(n => n.nodeName === datatype);
          if (node && node.depends && node.depends.length > 0) {
            // Ajouter toutes les dépendances du datatype
            node.depends.forEach(dep => result.add(dep));
          }
        }
      }

      return Array.from(result);
    });
    const buildDependencies = (hierarchicalNodes: Node[]) => {
      const dependenciesObject: any = {};

      // Initialiser l'objet avec tous les datatypes
      hierarchicalNodes.forEach((node) => {
        dependenciesObject[node.nodeName] = {
          dependencies: [],
          dependents: [],
        };
      });

      // Remplir les dépendances et les dépendants
      hierarchicalNodes.forEach((node) => {
        // Ajouter les dépendances
        if (node.depends && node.depends.length > 0) {
          dependenciesObject[node.nodeName].dependencies = node.depends;

          // Ajouter ce datatype comme dépendant pour chacune de ses dépendances
          node.depends.forEach((dep) => {
            if (dependenciesObject[dep]) {
              dependenciesObject[dep].dependents.push(node.nodeName);
            }
          });
        }

        // Gérer la relation parent-enfant
        if (node.parent) {
          dependenciesObject[node.nodeName].dependencies.push(node.parent);
          if (dependenciesObject[node.parent]) {
            dependenciesObject[node.parent].dependents.push(node.nodeName);
          }
        }

        // Gérer les enfants
        if (node.children && node.children.length > 0) {
          node.children.forEach((childName: any) => {
            if (dependenciesObject[childName]) {
              dependenciesObject[childName].dependencies.push(node.nodeName);
              dependenciesObject[node.nodeName].dependents.push(childName);
            }
          });
        }
      });

      return dependenciesObject;
    };

    watch(period, () => {
      endDate.value = null;
      startDate.value = null;
    });

    watch(currentAuthorization, () => {
      changeAuthorization(currentAuthorization.value);
    });

    onMounted(async () => {
      await init();
      const authenticatedUser = services.loginService.getStoredAuthenticatedUser();
      if (authenticatedUser) {
        let localUserCookie
          = authenticatedUser.currentUserRoles.applicationRoles[
            application.id
          ] || [];
        canCreateApplication.value
          = localUserCookie.includes("applicationManager") || localUserCookie.includes("userManager");
      }
      if (canCreateApplication.value) {
        changeSubMenuPaths([
          new SubMenuPath(
            t("menu.accueil").toLowerCase(),
            () => router.push(`/applications/${props.applicationName}`),
            () => router.push("/applications"),
          ),
          new SubMenuPath(
            t("dataTypeAuthorizations.sub-menu-request-authorization"),
            () => {
              router.push(`/applications/${props.applicationName}/authorizationsRequest`);
            },
            () => router.push(`/applications/${props.applicationName}`),
          ),
          new SubMenuPath(
            t("dataTypeAuthorizations.sub-menu-new-authorization"),
            () => {},
            () => {
              router.push(`/applications/${props.applicationName}/authorizationsRequest`);
            },
          ),
        ]);
      }
      else {
        changeSubMenuPaths([
          new SubMenuPath(
            t("menu.accueil").toLowerCase(),
            () => router.push(`/applications/${props.applicationName}`),
            () => router.push(`/applications/${props.applicationName}`),
          ),
          new SubMenuPath(
            t("dataTypeAuthorizations.sub-menu-new-authorization"),
            () => {},
            () => {
              router.push(`/applications/${props.applicationName}`);
            },
          ),
        ]);
      }
      datatypes.value = {
        withScope: Object.keys(application.dataTypes)
          .filter(name => application.configuration.dataDescription[name].authorization)
          .reduce((acc: any, dataType: string) => {
            acc[dataType] = {
              id: dataType,
              name:
                services.internationalisationService.localeReferenceNames(dataType, application)
                || dataType,
            };
            return acc;
          }, {}),
        withoutScope: Object.keys(application.dataTypes)
          .filter(name => application.configuration.dataDescription[name].authorization === null)
          .reduce((acc: any, dataType: string) => {
            acc[dataType] = {
              id: dataType,
              name:
                services.internationalisationService.localeReferenceNames(dataType, application)
                || dataType,
            };
            return acc;
          }, {}),
      };
      isLoading.value = false;
    });

    function initApplication(getApplication: LocalApplicationResult) {
      changeApplication(
        getApplication,
      );
      changeConfiguration(application.configuration);
      changeDatas(Object.keys(application.data).reduce((acc: any, data: string) => {
        acc[data] = {
          id: data,
          name:
            services.internationalisationService.localeReferenceNames(data, application) || data,
        };
        return acc;
      }, {}));
      for (let data of Object.keys(configuration.dataDescription) as [string]) {
        if (
          configuration?.dataDescription?.[data]
          && configuration?.dataDescription?.[data]?.authorization
        ) {
          // @ts-expect-error TODO: pending proper type annotation
          authorizations[data] = configuration.dataDescription[data].authorization;
        }
      }
    }

    async function init() {
      isLoading.value = true;
      try {
        const getApplication = await services.applicationService.getApplication(
          props.applicationName,
          ["DATATYPE", "REFERENCETYPE", "CONFIGURATION", "ADDITIONALFILE"],
        );
        if (getApplication) {
          initApplication(getApplication);
        }
        dependencies.value = buildDependencies(configuration.hierarchicalNodes);
        changeFormat(configuration.rightsRequest.formFields || {});
        changeFields(
          (Object.keys(format) || []).reduce((acc: any, field: string) => {
            acc[field] = "";
            return acc;
          }, {}),
        );
        let userPrefLocale = services.userPreferencesService.getUserPrefLocale();
        if (application?.internationalization.rightsrequest?.fields?.[userPrefLocale]?.description?.[userPrefLocale]) {
          description.value = application?.internationalization.rightsrequest?.fields[userPrefLocale].description[userPrefLocale];
        }
        else {
          description.value
            = t("dataTypeAuthorizations.field_form_description", {
              applicationName: application?.localName,
            });
        }

        const authenticatedUser = services.loginService.getStoredAuthenticatedUser();
        if (props.authorizationId !== "new") {
          let listAllUsers = await services.authorizationService.getAuthorizationGrantableInfos(
            props.applicationName,
          );
          let scopes = listAllUsers.referenceScopes;
          referencesScopes.value = Object.keys(scopes || {}).reduce((acc: any, dataName: string) => {
            if (scopes[dataName].length) {
              acc[dataName] = scopes[dataName];
            }
            return acc;
          }, {});
          valid = true;
          let request = await services.requestRightsService.getRightsRequests(
            props.applicationName,
            {
              uuids: [props.authorizationId],
            },
          );
          const userId
            = request.rightsRequests?.[0]?.user ?? authenticatedUser?.id;
          const foundUser
            = request.users.find(user => user.id === userId);
          currentUser.value = foundUser
            ? [foundUser]
            : authenticatedUser
              ? [authenticatedUser]
              : [];
          let rightsRequest = request.rightsRequests[0];
          comment.value = rightsRequest.comment;
          changeFields(
            (Object.keys(format) || []).reduce((acc: any, field: string) => {
              acc[field] = rightsRequest.rightsRequestForm[field];
              return acc;
            }, {}),
          );
          if (rightsRequest && Object.keys(rightsRequest.rightsRequest).length !== 0) {
            let authorizations = rightsRequest.rightsRequest;
            // @ts-expect-error TODO: pending proper type annotation
            currentAuthorization.value = {
              description: description.value,
              name: t("dataTypeAuthorizations.title", {
                label: authenticatedUser?.login,
              }),
              uuid: "",
              authorizations: {},
            };
            // Copier les autorisations existantes dans l'objet currentAuthorization
            Object.keys(authorizations).forEach((key) => {
              currentAuthorization.value.authorizations[key] = authorizations[key][0];
            });
            changeAuthorization(currentAuthorization.value);
          }
        }
        else {
          currentUser.value = authenticatedUser;
          // @ts-expect-error TODO: pending proper type annotation
          currentAuthorization.value = {};
        }
        initialized.value = true;
      }
      catch (error) {
        services.alertService.toastServerError(error as ErrorType);
      }
    }

    function updateFields(event: { fields: string[]; key: string }) {
      let count = 0;
      for (let formatKey in format) {
        if (formatKey === event.key && event.fields) {
          // @ts-expect-error TODO: pending proper type annotation
          fields[event.key] = event.fields;
        }
        // @ts-expect-error TODO: pending proper type annotation
        if (fields[formatKey] !== "") {
          count++;
        }
      }
      valid = count === Object.keys(format).length;
    }

    function updateComment(event: {
      comment: string;
      valid: boolean;
      errorKey?: string | null;
      errorParams?: Record<string, number>;
    }) {
      comment.value = event.comment;
      valid = event.valid;
      commentErrorKey.value = event.errorKey ?? null;
      commentErrorParams.value = event.errorParams ?? {};
    }

    async function grantAuthorization() {
      try {
        await createAuthorization();
      }
      catch (e) {
        console.log("error", e);
      }
      finally {
        await createRequest(true);
      }
    }

    function updateAuthorization(event: any) {
      let current = { ...currentAuthorization.value };
      current.authorizations = event;
      currentAuthorization.value = current;
    }

    const buildAuthorization = () => {
      const authorizationForAll: any = {};
      const authorizationsWithRestriction: any = {};
      const authorization: { [p: string]: AuthorizationParsed } = { ...currentAuthorization.value.authorizations };
      for (const [datatype, auth] of Object.entries(authorization)) {
        // Ignorer les datatypes sans opérationTypes ou avec seulement 'extraction' s'ils sont dans hasDependencies
        if (
          !auth.operationTypes
          || (JSON.stringify(auth.operationTypes) === "[\"extraction\"]"
            && hasDependencies?.value
            && hasDependencies.value.includes(datatype))
        ) {
          continue;
        }
        else {
          auth.operationTypes = [...auth.operationTypes];
        }
        // @ts-expect-error TODO: pending proper type annotation
        if (!auth.timescope && !auth.requiredAuthorizations) {
          authorizationForAll[datatype] = auth.operationTypes;
          continue;
        }

        // Vérifier si le timeScope est infini
        const isInfiniteTimeScope
          = auth.fromDay !== undefined
            && auth.fromDay[0] === -999999999
            && auth.toDay !== undefined
            && auth.toDay[0] === 999999999;

        // Vérifier si requiredAuthorizations est vide ou égal à [""]
        const hasNoRequiredAuths
          = !auth.requiredAuthorizations
            || Object.values(auth.requiredAuthorizations).every(
              (arr: any) => arr.length === 0 || (arr.length === 1 && arr[0] === ""),
            );

        if (isInfiniteTimeScope && hasNoRequiredAuths) {
          if (!auth.requiredAuthorizations) {
            authorizationForAll[datatype] = new Set(auth.operationTypes);
          }
        }
        else {
          authorizationsWithRestriction[datatype] = {
            operationTypes: auth.operationTypes,
            requiredAuthorizations: auth.requiredAuthorizations,
            timeScope: {},
          };

          // Ajouter fromDay seulement s'il n'est pas infini
          if (auth.fromDay && auth?.fromDay[0] !== -999999999) {
            authorizationsWithRestriction[datatype].timeScope.fromDay = `${
              auth.fromDay[0]
            }-${auth.fromDay[1].toString().padStart(2, "0")}-${auth.fromDay[2]
              .toString()
              .padStart(2, "0")}`;
          }

          // Ajouter toDay seulement s'il n'est pas infini
          if (auth.toDay && auth?.toDay?.[0] !== 999999999) {
            authorizationsWithRestriction[datatype].timeScope.toDay = `${
              auth.toDay[0]
            }-${auth.toDay[1].toString().padStart(2, "0")}-${auth.toDay[2]
              .toString()
              .padStart(2, "0")}`;
          }

          // Si timeScope est vide, le supprimer complètement
          if (Object.keys(authorizationsWithRestriction[datatype].timeScope).length === 0) {
            delete authorizationsWithRestriction[datatype].timeScope;
          }
        }
      }
      let usersId;
      let authenticatedUserId = services.loginService.getStoredAuthenticatedUser()?.id;
      if (
        (currentUser.value?.id && currentUser.value?.id !== authenticatedUserId)
        || (currentUser.value[0] && currentUser.value[0]?.id !== authenticatedUserId)
      ) {
        usersId = currentUser.value?.id
          ? [currentUser.value?.id, authenticatedUserId]
          : [currentUser.value[0]?.id, authenticatedUserId];
      }
      else {
        usersId = [currentUser.value?.id ? currentUser.value?.id : currentUser.value[0]?.id];
      }
      return {
        uuid: props.authorizationId === "new" ? null : props.authorizationId,
        name: t("dataTypeAuthorizations.title", {
          label: currentUser.value[0] ? currentUser.value[0].login : currentUser.value.label,
        }),
        description: description.value ? description.value : "null",
        usersId: usersId,
        authorizationForAll,
        authorizationsWithRestriction,
      };
    };

    async function createRequest(isSetted: boolean) {
      // Auparavant : return silencieux quand le commentaire était
      // vide / trop court , donnant l'impression que le bouton
      // "Enregistrer" ne réagissait pas . On affiche désormais un
      // toast i18n explicite ( cf commentValidator + FieldsForm
      // qui propagent {@code commentErrorKey} ) et on quitte tôt .
      if (!valid) {
        const errorKey = commentErrorKey.value ?? "rightsRequest.detail.required";
        services.alertService.toastErrorMessage(
          i18n.global.t(errorKey, commentErrorParams.value),
        );
        return;
      }
      try {
        let authorizationToSend = buildAuthorization();
        if (!(comment.value && comment.value.length)) {
          await new Promise((resolve) => {
            services.alertService.prompt({
              message: i18n.global.t("dataTypeAuthorizations.addComment"),
              inputAttrs: {
                placeholder: i18n.global.t("dataTypeAuthorizations.commentExample"),
                maxlength: 255,
                minLength: 3,
                canCancel: false,
                confirmText: i18n.global.t("dataTypeAuthorizations.grantRequestConfirm"),
              },
              trapFocus: true,
              // @ts-expect-error TODO: pending proper type annotation
              onConfirm: (value: string) => {
                comment.value = value;
                // @ts-expect-error TODO: pending proper type annotation
                resolve();
              },
              onCancel: () => {
                // @ts-expect-error TODO: pending proper type annotation
                resolve();
              },
            });
          });
        }
        await services.requestRightsService.createRequestRights(props.applicationName, {
          id: props.authorizationId === "new" ? null : props.authorizationId,
          fields: fields,
          rightsRequest: authorizationToSend as CreateAuthorizationRequest,
          // #466 - backend attend un boolean primitif ( record Java ) ;
          // envoyer undefined fait disparaitre le champ via JSON.stringify
          // et provoque un 400 "Failed to read request". On envoie false
          // pour les nouvelles demandes , true pour les mises a jour.
          setted: props.authorizationId !== "new",
          comment: comment.value,
        });
        if ("new" === props.authorizationId) {
          services.alertService.toastSuccess(i18n.global.t("alert.create-request"));
          await router.push(`/applications/${props.applicationName}`);
        }
        else if (isSetted) {
          services.alertService.toastSuccess(i18n.global.t("alert.valid-request"));
          await router.push(`/applications/${props.applicationName}/authorizations`);
        }
        else {
          services.alertService.toastSuccess(i18n.global.t("alert.modified-request"));
          await router.push(`/applications/${props.applicationName}`);
        }
      }
      catch (error) {
        services.alertService.toastServerError(error as ErrorType);
      }
    }

    async function createAuthorization() {
      try {
        let authorizationForAll: any = {};
        let authorizationsWithRestriction: any = {};
        for (let datatype in authorization.authorizations) {
          if (
            authorization.authorizations[datatype].requiredAuthorizations
            && Object.keys(authorization.authorizations[datatype].requiredAuthorizations).length !== 0
          ) {
            authorizationsWithRestriction[datatype] = {
              operationTypes: authorization.authorizations[datatype].operationTypes,
              requiredAuthorizations: authorization.authorizations[datatype].requiredAuthorizations,
              timeScope: {
                fromDay: authorization.authorizations[datatype].fromDay,
                toDay: authorization.authorizations[datatype].toDay,
              },
            };
          }
          else {
            authorizationForAll[datatype] = Object.values(
              authorization.authorizations[datatype].operationTypes,
            );
          }
        }
        const authenticatedUserId = services.loginService.getStoredAuthenticatedUser()?.id || "";
        let authorizationToSend = {
          uuid: "",
          name: `request ${props.authorizationId} for user ${
            currentUser.value.label ? currentUser.value.label : currentUser.value[0].label
          }`,
          description: description.value,
          usersId: [currentUser.value[0]?.id, authenticatedUserId],
          authorizationForAll,
          authorizationsWithRestriction,
        };
        await services.authorizationService.createorUpdateAuthorization(
          props.applicationName,
          authorizationToSend,
        );
      }
      catch (error) {
        services.alertService.toastServerError(error as ErrorType);
      }
      finally {
        services.alertService.toastSuccess(i18n.global.t("alert.create-authorization"));
      }
    }

    function confirmGrantAuthorization() {
      services.alertService.prompt({
        title: i18n.global.t("dataTypeAuthorizations.confirmGrantRequestsTitle"),
        message: i18n.global.t("dataTypeAuthorizations.confirmGrantRequests", currentUser.value[0]),
        inputAttrs: {
          placeholder: i18n.global.t("dataTypeAuthorizations.commentExample"),
          maxlength: 255,
          minLength: 3,
          canCancel: false,
          confirmText: "",
        },
        inputValue: description.value,
        confirmText: i18n.global.t("dataTypeAuthorizations.grantRequestConfirm"),
        cancelText: i18n.global.t("dataTypeAuthorizations.grantRequestDismiss"),
        trapFocus: true,
        // @ts-expect-error TODO: pending proper type annotation
        onConfirm: (value: string) => {
          if (value && value.trim().length > 0) {
            description.value = value;
            grantAuthorization();
          }
          else {
            services.alertService.toastError(i18n.global.t("dataTypeAuthorizations.grantRequestConfirm"));
          }
        },
      });
      currentAuthorization.value.description = description.value;
    }

    return {
      isLoading,
      subMenuPaths,
      description,
      comment,
      canCreateApplication,
      datatypes,
      application,
      currentUser,
      fields,
      format,
      references,
      authorization,
      referencesScopes,
      initialized,
      hasDependencies,
      authorizations,
      currentAuthorization,
      updateFields,
      updateComment,
      confirmGrantAuthorization,
      createRequest,
      updateAuthorization,
      COMMENT_MIN_LENGTH,
    };
  },
};
</script>
